import { Box, Typography, useTheme } from '@mui/material';
import { getMasterPhotoById, getTeamImageHdById } from '@/helpers/images-helper';
import CustomImageComponent from '@/components/shared/custom/custom-image';
import LottieIconComponent from '@/DesignSystem/features/lottie/LottieIconComponent';
import { LOTTIE_ICONS } from '@/DesignSystem/features/lottie/lottie-icons';
import { useMemo } from 'react';
import { EQUIPOS_LALIGA } from '@/data/equipos';
import CustomStoryImageWithDefault from '../../custom/CustomStoryImageWithDefault';

const StoryContent = ({ story, showImage = true }) => {
    const theme = useTheme();

    if (!story) {
        return null;
    }

    const getMainImage = (size) => {
        let mainImage = null;
        if (story.image) {
            mainImage = story.image;
        }
        if (story.type === 'Player' && story.playerId) {
            mainImage = getMasterPhotoById(story.playerId, size * 1.5);
        }
        if (story.type === 'Team' && story.teamId) {
            if (story.teamId === EQUIPOS_LALIGA.REAL_MADRID) {
                return <LottieIconComponent icon={LOTTIE_ICONS.REAL_MADRID} width={150} height={150} />;
            } else if (story.teamId === EQUIPOS_LALIGA.BARCELONA) {
                return <LottieIconComponent icon={LOTTIE_ICONS.BARCELONA} width={150} height={150} />;
            } else if (story.teamId === EQUIPOS_LALIGA.ATLETICO_MADRID) {
                return <LottieIconComponent icon={LOTTIE_ICONS.ATLETICO} width={150} height={150} />;
            }

            mainImage = getTeamImageHdById(story.teamId, size * 1.5);
        }

        return (
            <CustomStoryImageWithDefault
                src={`${mainImage}?width=225&height=225`}
                alt={story.title || 'Story'}
                width={150}
                height={150}
                style={{ objectFit: 'cover', width: '100%', height: '100%' }}
            />
        );
    };

    const mainImage = useMemo(() => getMainImage(150), [story]);

    // Gradiente de fondo según el tipo
    const getBackgroundGradient = () => {
        if (story.type === 'Player') {
            return 'linear-gradient(135deg, #020A1A 0%, #051A3A 100%)';
        }
        if (story.type === 'Team') {
            return 'linear-gradient(135deg, #020A1A 0%, #4A0E2E 100%)';
        }
        return 'linear-gradient(135deg, #020A1A 0%,rgb(58, 5, 27) 100%)';
    };

    return (
        <Box
            sx={{
                background: getBackgroundGradient(),
                borderRadius: 2,
                p: 3,
                width: '100%',
                position: 'relative',
            }}
        >
            {/* Icono de fuego si es hot */}
            {story.isHot && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        zIndex: 2,
                    }}
                >
                    <LottieIconComponent icon={LOTTIE_ICONS.FIRE} width={64} height={64} />
                </Box>
            )}

            {/* Imagen grande */}
            {showImage && mainImage && (
                <Box
                    sx={{
                        width: '100%',
                        maxWidth: 150,
                        height: 150,
                        margin: '0 auto 2rem',
                        borderRadius: 2,
                        overflow: 'hidden',
                    }}
                >
                    {mainImage}
                </Box>
            )}

            {/* Título */}
            {story.url ? (
                <Typography
                    component="a"
                    href={story.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="h6"
                    fontWeight={500}
                    mb={1}
                    textAlign="center"
                    sx={{
                        textDecoration: 'underline',
                        color: theme.palette.primary.main,
                        cursor: 'pointer',
                        display: 'block',
                        '&:hover': {
                            opacity: 0.8,
                        },
                        fontSize: '16px',
                    }}
                >
                    {story.title}
                </Typography>
            ) : (
                <Typography variant="h6" fontWeight={600} mb={1} textAlign="center">
                    {story.title}
                </Typography>
            )}

            {/* Descripción */}
            {story.description && (
                <Typography textAlign="center" color={'rgba(255, 255, 255, 0.8)'}>
                    {story.description}
                </Typography>
            )}

            {/* Items */}
            {story.items && story.items.length > 0 && (
                <Box
                    sx={{
                        mt: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        gap: 1,
                        alignItems: 'center',
                    }}
                >
                    {story.items.map((item, index) => (
                        <Typography key={index} textAlign="center" color={'rgba(255, 255, 255, 0.8)'}>
                            {item}
                        </Typography>
                    ))}
                </Box>
            )}
        </Box>
    );
};

export default StoryContent;
